import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  Points,
  RingGeometry,
  ShaderMaterial,
} from 'three';
import { Layer } from './Layer.js';
import { mulberry32 } from './Seashells.js';

/**
 * What is outside the windows.
 *
 * Isidora is the city the traveller imagined while riding through savage
 * lands, so there are two of it: the plain dark one that is actually there,
 * and — brought close by a telescope — a second, brighter one laid a hand's
 * breadth to the side of it, with more of its windows lit. The lens does not
 * magnify the city. It magnifies the wanting.
 */
export class DreamCity extends Layer {
  static id = 'dream-city';
  static order = 30;

  async build() {
    const { settings, palette, helix } = this.ctx;
    const cfg = settings.dreamCity;
    this.cfg = cfg;

    const groundY = helix.topY - settings.stair.height * 0.72;
    this.groundY = groundY;
    const rand = mulberry32(0x0151d0);

    // ── the towers that are there ────────────────────────────────────────
    const prism = new CylinderGeometry(0.74, 1, 1, 4, 1);
    prism.rotateY(Math.PI / 4);
    prism.translate(0, 0.5, 0);

    this.towerMat = new MeshStandardMaterial({
      color: palette.get('stoneDeep').clone(),
      roughness: 1,
      metalness: 0,
    });
    const towers = new InstancedMesh(prism, this.towerMat, cfg.towers);
    towers.frustumCulled = false;

    // ── the same towers, as they were wanted ─────────────────────────────
    // The dreamed city is not subject to the weather. The real one is 120 m
    // off through dusk air and reads as haze; this one does not, which is
    // the whole difference between a city and the memory of wanting it.
    this.ghostMat = new MeshBasicMaterial({
      color: palette.get('desire').clone(),
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
    });
    const ghosts = new InstancedMesh(prism.clone(), this.ghostMat, cfg.towers);
    ghosts.frustumCulled = false;

    // Decide the skyline first: a spire instance with zero height would have
    // a singular matrix, whose inverse-transpose is NaN — and one NaN pixel,
    // blurred by the bloom, takes the entire frame with it.
    const spireOf = [];
    let spireCount = 0;
    for (let i = 0; i < cfg.towers; i++) {
      const h = rand() < 0.42 ? 0.18 + rand() * 0.4 : 0;
      spireOf.push(h);
      if (h > 0) spireCount++;
    }
    const spireGeo = new ConeGeometry(0.62, 1, 4, 1);
    spireGeo.rotateY(Math.PI / 4);
    spireGeo.translate(0, 0.5, 0);
    const spires = new InstancedMesh(spireGeo, this.towerMat, spireCount);
    spires.frustumCulled = false;
    let spireIndex = 0;

    const dummy = new Object3D();
    const lightPts = [];
    for (let i = 0; i < cfg.towers; i++) {
      const a = rand() * Math.PI * 2;
      // Dense near the shaft, thinning toward the edge of what can be seen.
      const r = cfg.innerRadius + Math.pow(rand(), 0.55) * (cfg.radius - cfg.innerRadius);
      const h = cfg.minHeight + Math.pow(rand(), 2.0) * (cfg.maxHeight - cfg.minHeight);
      const w = 6 + rand() * 16 + h * 0.05;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;

      dummy.position.set(x, groundY, z);
      dummy.rotation.set(0, rand() * Math.PI, 0);
      dummy.scale.set(w, h, w * (0.7 + rand() * 0.6));
      dummy.updateMatrix();
      towers.setMatrixAt(i, dummy.matrix);

      // Laid a little to the side of the real one, and taller: this is the
      // city he imagined while riding, not the one he reached.
      dummy.scale.multiplyScalar(1.02);
      dummy.scale.y *= 1.18;
      dummy.position.x += 5.0;
      dummy.position.y += 2.0;
      dummy.updateMatrix();
      ghosts.setMatrixAt(i, dummy.matrix);

      // Some of them carry a spire: a city of telescopes has a skyline.
      if (spireOf[i] > 0) {
        dummy.position.set(x, groundY + h, z);
        dummy.rotation.set(0, rand() * Math.PI, 0);
        dummy.scale.set(w * 0.78, h * spireOf[i], w * 0.78);
        dummy.updateMatrix();
        spires.setMatrixAt(spireIndex++, dummy.matrix);
      }

      // Lit windows, scattered over the faces.
      const windows = Math.round(3 + rand() * 12);
      for (let k = 0; k < windows; k++) {
        const side = Math.floor(rand() * 4);
        const off = (rand() - 0.5) * w * 0.9;
        const wy = groundY + rand() * h * 0.95;
        const px = side === 0 ? x + w * 0.5 : side === 1 ? x - w * 0.5 : x + off;
        const pz = side === 2 ? z + w * 0.5 : side === 3 ? z - w * 0.5 : z + off;
        lightPts.push(px, wy, pz, rand());
      }
    }
    towers.instanceMatrix.needsUpdate = true;
    ghosts.instanceMatrix.needsUpdate = true;
    spires.instanceMatrix.needsUpdate = true;
    this.towers = towers;
    this.ghosts = ghosts;
    this.group.add(towers, spires, ghosts);

    this._buildWindows(lightPts);
    this._buildGround(groundY, cfg.radius);
  }

  _buildWindows(flat) {
    const count = flat.length / 4;
    const pos = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = flat[i * 4];
      pos[i * 3 + 1] = flat[i * 4 + 1];
      pos[i * 3 + 2] = flat[i * 4 + 2];
      seed[i] = flat[i * 4 + 3];
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new BufferAttribute(seed, 1));

    this.windowMat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uScale: { value: 1 },
        uWarm: { value: new Color(0xffb46a) },
        uDream: { value: new Color(0xbfd8ff) },
        uIris: { value: 0 },
        uOpacity: { value: 0.8 },
      },
      vertexShader: /* glsl */ `
        attribute float aSeed;
        uniform float uTime, uScale, uIris;
        varying float vSeed;
        varying float vFlicker;
        void main() {
          vSeed = aSeed;
          // Not all of them are awake at once.
          float phase = sin(uTime * (0.08 + aSeed * 0.22) + aSeed * 53.0);
          vFlicker = smoothstep(-0.35, 0.6, phase) * (0.75 + 0.25 * sin(uTime * 3.1 + aSeed * 90.0));
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
          float dist = max(-mv.z, 1.0);
          // Through an eyepiece the city's windows are the only thing with
          // enough light to survive the dusk, so they swell — but a window
          // is still a window, and must not become a floodlight.
          gl_PointSize = min((90.0 + uIris * 900.0) * uScale / dist, 24.0 * uScale);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uWarm, uDream;
        uniform float uIris, uOpacity;
        varying float vSeed;
        varying float vFlicker;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float r = length(d);
          float a = smoothstep(0.5, 0.02, r);
          a *= a;
          vec3 col = mix(uWarm, uDream, uIris * 0.65 + step(0.93, vSeed) * 0.3);
          gl_FragColor = vec4(col, a * vFlicker * uOpacity);
        }
      `,
    });
    const pts = new Points(geo, this.windowMat);
    pts.frustumCulled = false;
    this.windows = pts;
    this.group.add(pts);
  }

  _buildGround(groundY, radius) {
    this.groundMat = new MeshStandardMaterial({
      color: new Color(0x0a0d18),
      roughness: 1,
      metalness: 0,
    });
    const plain = new Mesh(new RingGeometry(18, radius * 3.2, 72, 1), this.groundMat);
    plain.rotation.x = -Math.PI / 2;
    plain.position.y = groundY;
    this.group.add(plain);
  }

  update(dt, state) {
    const { palette } = this.ctx;
    const iris = state.iris;
    this.towerMat.color.copy(palette.get('stoneDeep')).multiplyScalar(0.42);
    this.windowMat.uniforms.uTime.value = state.elapsed;
    this.windowMat.uniforms.uScale.value = state.pixelRatio;
    this.windowMat.uniforms.uIris.value = iris;
    this.windowMat.uniforms.uWarm.value.copy(palette.get('lamp'));
    this.windowMat.uniforms.uDream.value.copy(palette.get('desire'));
    this.windowMat.uniforms.uOpacity.value = 0.5 + state.t * 0.5 + iris * 0.35;

    // The dreamed city only exists while something is magnifying the wanting.
    this.ghostMat.opacity = Math.pow(iris, 1.3) * 0.22;
    this.ghostMat.color.copy(palette.get('desire'));
    this.groundMat.color.copy(palette.get('void')).multiplyScalar(0.8);
  }

  stats() {
    return { towers: this.cfg.towers, windows: this.windows?.geometry.attributes.position.count ?? 0 };
  }
}
